import type { TransactionProperty } from '@popety-io/types'

export type TransactionAddressProperty = Pick<
  TransactionProperty,
  'land_address' | 'land_code_number'
>

type TransactionWithProperties<TProperty extends TransactionAddressProperty> = {
  transaction_properties?: TProperty[] | null
}

export type TransactionAddress<
  TProperty extends TransactionAddressProperty = TransactionProperty,
> = {
  type: 'address' | 'plotNumber'
  value: string
  property: TProperty
}

/**
 * Resolve the location identifier shown for a transaction.
 *
 * Prefer the first populated structured address across every property. When
 * no address exists, fall back exclusively to the first property's plot
 * number.
 */
export const getTransactionAddress = <
  TProperty extends TransactionAddressProperty,
>(
  transaction: TransactionWithProperties<TProperty>
): TransactionAddress<TProperty> | undefined => {
  const properties = transaction.transaction_properties ?? []
  const addressProperty = properties.find((property) =>
    Boolean(property.land_address?.trim())
  )

  if (addressProperty?.land_address?.trim()) {
    return {
      type: 'address',
      value: addressProperty.land_address.trim(),
      property: addressProperty,
    }
  }

  const firstProperty = properties[0]
  const plotNumber = firstProperty?.land_code_number?.trim()

  if (!firstProperty || !plotNumber) return undefined

  return {
    type: 'plotNumber',
    value: plotNumber,
    property: firstProperty,
  }
}

// Swiss street-type keywords (full words + common abbreviations) used to
// locate the street inside the free-text transaction description. The type
// must be followed by whitespace, a dot (abbreviations like "ch."/"av."), or
// the end of the token ("Grand-Rue"). That lookahead also stops the ASCII "\b"
// from misfiring on accented commune names such as "Chêne-Bougeries".
const STREET_TYPES = [
  'rue',
  'ruelle',
  'grand-rue',
  'route',
  'rte',
  'chemin',
  'ch',
  'avenue',
  'av',
  'boulevard',
  'bd',
  'place',
  'plateau',
  'parc',
  'mail',
  'quai',
  'rampe',
  'impasse',
  'promenade',
  'sentier',
  'passage',
  'clos',
  'cour',
  'allee',
  'allée',
  'esplanade',
  'montee',
  'montée',
  'cote',
  'côte',
  'vy',
]

const STREET_RE = new RegExp(`^(?:${STREET_TYPES.join('|')})(?=\\s|\\.|$)`, 'i')

// A house-number token: digits with an optional letter ("3A", "40E") and an
// optional range ("89-91").
const HOUSE_NUMBER_RE = /^\d+[a-z]?(?:-\d+[a-z]?)?$/i

const capitalize = (s: string): string =>
  s.length ? s.charAt(0).toUpperCase() + s.slice(1) : s

/**
 * Extract a displayable street address from a transaction's free-text
 * `transaction_step_text`.
 *
 * The structured `transaction_properties` carry no street, so the only source
 * is the description. Genève records expose it in a `Commune et lieu:` segment
 * (`<commune>, <number>, <street>`, with several shapes); Vaud (and other)
 * records have no street at all. Returns `null` when none can be found, so
 * callers fall back to commune / parcel.
 *
 * The Swiss display convention is "<Street> <Number>", so a leading house
 * number is moved after the street name.
 */
export const getTransactionStreetAddress = (
  text: string | null | undefined
): string | null => {
  if (!text) return null

  const match = /Commune et lieu:\s*([^|]+)/i.exec(text)
  if (!match) return null

  // Keep only the first address when several are listed (";"-separated).
  const segment = match[1].split(';')[0].trim()
  if (!segment) return null

  // Street-first shape with no commune/number commas, e.g. "rue Beau-Site 1",
  // "rte de Vireloup 89-91", "Route d'Aire-la-Ville". The whole segment is the
  // street — keep it if it looks like one (known type, or has a house number).
  if (!segment.includes(',')) {
    return STREET_RE.test(segment) || /\d/.test(segment)
      ? capitalize(segment)
      : null
  }

  // Comma shape: [commune,] [number,] street.
  const parts = segment
    .split(',')
    .map((p) => p.trim())
    .filter(Boolean)

  // Structural rule first: the token right after a house number is the street,
  // even when its name carries no street type ("Grand-Montfleury", "Plateau …").
  const numIdx = parts.findIndex((p) => HOUSE_NUMBER_RE.test(p))
  if (numIdx !== -1 && parts[numIdx + 1]) {
    return capitalize(`${parts[numIdx + 1]} ${parts[numIdx]}`)
  }

  // No house number: pick the token that starts with a street type, skipping a
  // leading commune ("Veyrier, chemin des Etournelles").
  const streetIdx = parts.findIndex((p) => STREET_RE.test(p))
  return streetIdx === -1 ? null : capitalize(parts[streetIdx])
}
